import { CircleCheck, CircleDashed, CircleGauge, CircleX } from 'lucide-react';
import { useState } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/shadcn/dropdown-menu';

export type TaskStatusRowSecondarySection<SecondaryT extends string> = {
  label: string;
  value: SecondaryT;
  options: ReadonlyArray<SecondaryT>;
  onChange(next: SecondaryT): void;
  icon?: (value: SecondaryT) => React.ReactNode;
};

export type TaskStatusRowProps<StatusT extends string, SecondaryT extends string = never> = {
  label: string;
  status: StatusT;
  statusOptions: ReadonlyArray<StatusT>;
  onStatusChange(next: StatusT): void;
  secondary?: TaskStatusRowSecondarySection<SecondaryT>;

  /**
   * Optional additional content on the left (e.g., urgency indicator).
   */
  leftAccessory?: React.ReactNode;

  /**
   * Optional additional content on the right (e.g., edit/delete buttons).
   */
  rightAccessory?: React.ReactNode;

  /** When true, show status icon but disable the dropdown. */
  readonly?: boolean;
};

export function TaskStatusRow<StatusT extends string, SecondaryT extends string = never>({
  label,
  status,
  statusOptions,
  onStatusChange,
  secondary,
  leftAccessory,
  rightAccessory,
  readonly = false,
}: TaskStatusRowProps<StatusT, SecondaryT>) {
  const [open, setOpen] = useState(false);

  const statusIcon = (value: StatusT) => {
    // CareState Request statuses: Open | Pending Follow Up | Resolved | Cancelled
    switch (value) {
      case 'Open' as StatusT:
        return (
          <CircleDashed className="h-3 w-3 shrink-0 text-muted-foreground/70" strokeWidth={2.5} />
        );
      case 'Pending Follow Up' as StatusT:
        return <CircleGauge className="h-3 w-3 shrink-0 text-yellow-600" strokeWidth={2.5} />;
      case 'Resolved' as StatusT:
        return <CircleCheck className="h-3 w-3 shrink-0 text-green-700" strokeWidth={2.5} />;
      case 'Cancelled' as StatusT:
        return <CircleX className="h-3 w-3 shrink-0 text-red-700" strokeWidth={2.5} />;
      default:
        return (
          <CircleDashed className="h-3 w-3 shrink-0 text-muted-foreground/70" strokeWidth={2.5} />
        );
    }
  };

  return (
    <div className="flex h-4.5 min-w-0 flex-1 items-center gap-1">
      {leftAccessory ?? null}
      {readonly ? (
        <span
          className="flex h-3.5 w-3.5 shrink-0 items-center justify-center"
          aria-label={`Current status: ${status}`}
        >
          {statusIcon(status)}
        </span>
      ) : (
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded hover:bg-muted/40"
              onPointerEnter={() => {
                setOpen(true);
              }}
              onClick={(e) => {
                e.stopPropagation();
                setOpen((prev) => !prev);
              }}
              aria-label={`Current status: ${status}`}
            >
              {statusIcon(status)}
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            side="right"
            align="start"
            className="w-auto min-w-[9rem]"
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenuLabel className="py-0.5 text-[11px]">Status</DropdownMenuLabel>
            {statusOptions.map((opt) => (
              <DropdownMenuItem
                key={opt}
                className="flex items-center gap-1.5 text-[11px]"
                onClick={() => {
                  onStatusChange(opt);
                }}
              >
                <span className="flex h-3 w-3 items-center justify-center">{statusIcon(opt)}</span>
                <span className="min-w-0 truncate">{opt}</span>
              </DropdownMenuItem>
            ))}

            {secondary ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="py-0.5 text-[11px]">
                  {secondary.label}
                </DropdownMenuLabel>
                {secondary.options.map((opt) => (
                  <DropdownMenuItem
                    key={opt}
                    className="flex items-center gap-1.5 text-[11px]"
                    onClick={() => {
                      secondary.onChange(opt);
                    }}
                  >
                    <span className="flex h-3 w-3 items-center justify-center">
                      {secondary.icon ? secondary.icon(opt) : null}
                    </span>
                    <span className="min-w-0 truncate">{opt}</span>
                  </DropdownMenuItem>
                ))}
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <div className="min-w-0 flex-1 truncate text-[11px] font-semibold">{label}</div>

      {rightAccessory ?? null}
    </div>
  );
}
